/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Link } from "react-router-dom";

// @material-ui/core components
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import { SpeedDial, SpeedDialIcon, SpeedDialAction } from "@material-ui/lab";

import {
  Close,
  ZoomIn,
  ZoomOut,
  YoutubeSearchedFor,
  ExpandLess,
  ExpandMore,
  Replay10,
  OndemandVideo,
  CancelPresentation,
} from "@material-ui/icons";

import { getLicensedDoc, ApricotAction } from "client/actions/infobox.ts";
import { selectorPDFContent } from "client/selectors/infoboxSelector";
import { progressCircles } from "components/Form/FormField";
import { PdfLib } from "./PdfLib";
import { PdfViewer } from "./PDFViewer";
import {
  idClose,
  idPdfDial,
  idVideoDial,
  idPdfPip,
  idPdfZoomIn,
  idPdfZoomOut,
  idPdfZoomReset,
  idPdfNext,
  idPdfPrev,
} from "assets/jss/views/ElementIDs";

import iboxContainerStyle from "assets/jss/views/infobox/hdoViewerStyle";
const styles = createStyles(iboxContainerStyle);

type DocInfo = {
  count: number; // page count
  scale: number; // best fit scale
};

interface Props extends WithStyles<typeof styles> {
  getLicensedDoc: ApricotAction;
  // store data
  user: any;
  pdfContent: any;
  // component props
  playerStarted?: boolean;
  playerPip?: boolean;
  inStudy?: boolean;
  contentLink: string;
  onAction: (params: object) => void;
  classes: any;
}

const HandoutViewerBase: React.FC<Props> = ({
  getLicensedDoc,
  user,
  pdfContent,
  playerStarted = false,
  playerPip = false,
  inStudy = false,
  contentLink,
  onAction,
  classes,
}: Props) => {
  // current displaying parameters
  const [pageNumber, setPageNumber] = useState(1);
  const [pageScale, setPageScale] = useState(0);
  const maxScale = 40;
  const minScale = 0;

  // controls
  const [pdfDialOpen, setPdfDialOpen] = useState(false);
  const [videoDialOpen, setVideoDialOpen] = useState(false);

  const [pdfjsLib, setPdfJsLib] = useState<PdfLib>();
  const [docInfo, setDocInfo] = useState<DocInfo>({ count: 0, scale: 1 });
  const [pdfReady, setPdfReady] = useState(false);

  useEffect(() => {
    if (contentLink) {
      setPdfJsLib(new PdfLib());
      getLicensedDoc({
        docpath: contentLink,
        user: user.name,
        email: user.email,
        cr: true, // copyright text needed
      });
    }
  }, []);

  useEffect(() => {
    if (pdfContent && pdfContent.contentLink === contentLink && pdfjsLib) {
      (async function getInfo() {
        const { pageWidth, pageHeight, pageCount } = await pdfjsLib.setPdfDoc(
          pdfContent,
        );
        const scale = Math.min(
          Math.floor((window.innerWidth / pageWidth) * 10),
          Math.floor((window.innerHeight / pageHeight) * 10),
        );
        setDocInfo({ scale, count: pageCount });
        setPageScale(scale); // initialize current scale as the best fit
        setPdfReady(true);
      })();
    }
  }, [pdfContent, pdfjsLib]);

  const viewHandout = () => (
    <div style={{ position: "relative" }}>
      <PdfViewer
        pdfDoc={pdfjsLib && pdfjsLib.getDoc()}
        pdfPageNum={pageNumber}
        pdfPageScale={pageScale / 10}
      />
    </div>
  );

  const handleClose = () => {
    if (playerPip) {
      // close Pip if it's on
      onAction({ action: "setPip", bool: false });
    }
    onAction({ action: "ibx", str: null });
  };

  const handlePdfDial = (e: any, open: boolean) => {
    e.stopPropagation();
    setPdfDialOpen(open);
  };

  const handleVideoDial = (e: any, open: boolean) => {
    e.stopPropagation();
    setVideoDialOpen(open);
  };

  const pdfDialItems = () => [
    {
      icon: <ExpandMore id={idPdfNext} />,
      name: "Next Page",
      action: () => setPageNumber(pageNumber + 1),
      disabled: pageNumber === docInfo.count,
    },
    {
      icon: <ExpandLess id={idPdfPrev} />,
      name: "Previous Page",
      action: () => setPageNumber(pageNumber - 1),
      disabled: pageNumber === 1,
    },
    {
      icon: <ZoomIn id={idPdfZoomIn} />,
      name: "Zoom In",
      action: () => setPageScale(pageScale + 2),
      disabled: pageScale >= maxScale,
    },
    {
      icon: <ZoomOut id={idPdfZoomOut} />,
      name: "Zoom Out",
      action: () => setPageScale(pageScale - 2),
      disabled: pageScale <= minScale,
    },

    {
      icon: <YoutubeSearchedFor id={idPdfZoomReset} />,
      name: "Reset Zoom",
      action: () => setPageScale(docInfo.scale),
      disabled: false,
    },
  ];

  const videoDialItems = () => [
    {
      icon: (
        <img
          className={classes.playButton}
          src={require("assets/svg/playpause.svg")}
        />
      ),
      name: "Play/Pause Video",
      action: () => onAction({ action: "play" }),
      disabled: !playerStarted,
    },
    {
      icon: <Replay10 />,
      name: "Replay 10sec",
      action: () => onAction({ action: "play10", num: -10 }),
      disabled: !playerStarted,
    },
    {
      icon: playerPip ? (
        <CancelPresentation id={idPdfPip} />
      ) : (
        <OndemandVideo id={idPdfPip} />
      ),
      name: playerPip ? "Hide Video Player" : "Show Video Player",
      action: () => onAction({ action: "setPip", bool: !playerPip }),
      disabled: false,
    },
  ];

  const ibxVideoDial = () => (
    <SpeedDial
      ariaLabel="Clipboard video control"
      FabProps={{ color: "secondary" }}
      icon={<SpeedDialIcon id={idVideoDial} />}
      onClose={(e) => handleVideoDial(e, false)}
      onOpen={(e) => handleVideoDial(e, true)}
      open={videoDialOpen}>
      {videoDialItems().map((item: any) => (
        <SpeedDialAction
          classes={{ fab: classes.dialGreen }}
          FabProps={{ disabled: item.disabled }}
          key={item.name}
          icon={item.icon}
          tooltipTitle={item.name}
          onClick={(e: any) => {
            e.stopPropagation();
            item.action();
          }}
        />
      ))}
    </SpeedDial>
  );

  const ibxPdfDial = () => (
    <SpeedDial
      ariaLabel="Clipboard item control"
      icon={<SpeedDialIcon id={idPdfDial} />}
      direction={"left"}
      onClose={(e) => handlePdfDial(e, false)}
      onOpen={(e) => handlePdfDial(e, true)}
      open={pdfDialOpen}>
      {pdfDialItems().map((item: any, idx: number) => (
        <SpeedDialAction
          classes={{ fab: classes.dialOrange }}
          FabProps={{ disabled: item.disabled }}
          key={item.name}
          icon={item.icon}
          tooltipTitle={item.name}
          tooltipPlacement={"top"}
          onClick={(e: any) => {
            e.stopPropagation();
            item.action();
          }}
        />
      ))}
    </SpeedDial>
  );

  const ibxViewerCtrl = () => (
    <div className={classes.ibxDial}>
      {!inStudy && ibxVideoDial()}
      <>
        <span className={classes.hoPageNum}>
          {pageNumber}&nbsp;/&nbsp;{docInfo.count}
        </span>
        {ibxPdfDial()}
      </>
    </div>
  );

  const content = pdfReady ? (
    <>
      {viewHandout()}
      {ibxViewerCtrl()}
    </>
  ) : (
    <div style={{ position: "absolute", width: "100%" }}>
      {progressCircles()}
    </div>
  );

  return (
    <Dialog
      fullScreen={true}
      open={true}
      onClose={handleClose}
      scroll={"paper"}>
      <DialogTitle
        id="scroll-dialog-title"
        classes={{ root: classes.titleBarPDF }}>
        <div className={classes.titleText}>
          <Link to="/">
            <img
              src={require("assets/svg/apricot.svg")}
              className={classes.logo}
            />
          </Link>
          Handout Viewer
          <IconButton color="inherit" onClick={handleClose}>
            <Close id={idClose} />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
    </Dialog>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    pdfContent: selectorPDFContent(state),
  };
}

const Base: any = HandoutViewerBase;
export const HandoutViewer: any = withStyles(styles)(
  connect(mapStateToProps, { getLicensedDoc })(Base),
);

export const HandoutViewerTest = withStyles(styles)(Base);
